import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Calendar, Star, Filter, X, Search, MapPin, Sparkles, User as UserIcon } from 'lucide-react';
import { Therapist, Specialty } from '../types/user';
import { AppointmentModal } from '../components/appointment-modal';
import { fetchTherapists, fetchPrimaryTherapist } from '../api/therapist';
import { Alert, AlertDescription } from '../components/ui/alert';
import { TherapistAvatar } from '../components/ui/therapist-avatar';
import { formatNextAvailable } from '../utils/date';

const specialties: Specialty[] = [
  { id: '1', name: 'Développement Personnel', color: 'bg-blue-100 text-blue-800' },
  { id: '2', name: 'Vie Professionnelle', color: 'bg-purple-100 text-purple-800' },
  { id: '3', name: 'Relations', color: 'bg-pink-100 text-pink-800' },
  { id: '4', name: 'Stress', color: 'bg-yellow-100 text-yellow-800' },
  { id: '5', name: 'Confiance en Soi', color: 'bg-green-100 text-green-800' },
  { id: '6', name: 'Bien-être', color: 'bg-teal-100 text-teal-800' },
  { id: '7', name: 'Équilibre Vie Pro/Perso', color: 'bg-indigo-100 text-indigo-800' },
  { id: '8', name: 'Motivation', color: 'bg-orange-100 text-orange-800' }
];

const TherapistCard = ({ therapist }: { therapist: Therapist }) => {
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [bioTruncated, setBioTruncated] = useState(false);
  const [showAllSpecialties, setShowAllSpecialties] = useState(false);
  const bioRef = useRef<HTMLParagraphElement>(null);
  const [imageFailed, setImageFailed] = useState(!therapist.imageUrl);

  useLayoutEffect(() => {
    const el = bioRef.current;
    if (el && !bioExpanded) {
      setBioTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [bioExpanded, therapist.personalizedBio, therapist.bio]);

  return (
    <Card className="hover:shadow-lg transition-all duration-300 border-transparent hover:border-[#00c4cc]/20 overflow-hidden group">
      <CardContent className="p-0">
        <div className="flex flex-col md:flex-row">
          {/* Image Column */}
          <div className="w-full md:w-[200px] h-72 md:h-auto relative overflow-hidden">
            {imageFailed ? (
              <div className="w-full h-full bg-cyan-50 flex items-center justify-center">
                <UserIcon className="w-16 h-16 text-[#00c4cc]/40" />
              </div>
            ) : (
              <img
                src={therapist.imageUrl}
                alt={therapist.name}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                onError={() => setImageFailed(true)}
              />
            )}
          </div>

          {/* Content Column */}
          <div className="flex-1 p-6 flex flex-col justify-between">
            <div>
              <div className="flex flex-col md:flex-row justify-between items-start gap-2 mb-2">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#00c4cc] transition-colors">{therapist.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                    <div className="flex items-center text-yellow-500">
                      <Star className="w-4 h-4 fill-current" />
                      <span className="font-medium ml-1 text-gray-900">{therapist.rating}</span>
                    </div>
                    <span className="text-gray-300">|</span>
                    <span>{therapist.yearsExperience} ans d'expérience</span>
                  </div>
                </div>
                <div className="hidden md:flex flex-col items-end">
                  <div className="text-sm font-medium text-[#00c4cc] bg-cyan-50 px-3 py-1 rounded-full">
                    {therapist.specialties[0]?.name}
                  </div>
                </div>
              </div>

              <div
                className={`mb-4 ${bioTruncated || bioExpanded ? 'cursor-pointer' : ''}`}
                onClick={() => { if (bioTruncated || bioExpanded) setBioExpanded(!bioExpanded); }}
              >
                {therapist.personalizedBio && (
                  <span className="inline-flex items-center gap-1 text-xs text-[#00c4cc] font-medium mb-1">
                    <Sparkles className="w-3 h-3" />
                    Pour vous
                  </span>
                )}
                <p
                  ref={bioRef}
                  className={`text-gray-600 text-sm leading-relaxed ${!bioExpanded ? 'line-clamp-4' : ''}`}
                >
                  {therapist.personalizedBio || therapist.bio}
                </p>
                {bioTruncated && !bioExpanded && (
                  <span className="text-xs text-[#00c4cc] mt-1 inline-block">Voir plus</span>
                )}
                {bioExpanded && (
                  <span className="text-xs text-[#00c4cc] mt-1 inline-block">Voir moins</span>
                )}
              </div>

              <div
                className={`flex flex-wrap gap-2 mb-4 ${therapist.specialties.length > 3 ? 'cursor-pointer' : ''}`}
                onClick={() => { if (therapist.specialties.length > 3) setShowAllSpecialties(!showAllSpecialties); }}
              >
                {(showAllSpecialties ? therapist.specialties : therapist.specialties.slice(0, 3)).map((specialty) => (
                  <Badge
                    key={specialty.id}
                    variant="secondary"
                    className="bg-gray-100 text-gray-600 font-normal"
                  >
                    {specialty.name}
                  </Badge>
                ))}
                {therapist.specialties.length > 3 && (
                  <Badge
                    variant="outline"
                    className="text-gray-400 font-normal"
                  >
                    {showAllSpecialties ? 'Moins' : `+${therapist.specialties.length - 3}`}
                  </Badge>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-50 mt-2">
              <div className="flex items-center gap-4 text-sm text-gray-500 w-full sm:w-auto">
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span>Visio</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>Dispo. {formatNextAvailable(therapist.nextAvailable)}</span>
                </div>
              </div>

              <Button
                className="w-full sm:w-auto bg-[#00c4cc] hover:bg-[#00adc0] text-white shadow-md hover:shadow-lg transition-all"
                onClick={() => setShowAppointmentModal(true)}
              >
                Prendre rendez-vous
              </Button>
            </div>
          </div>
        </div>

        <AppointmentModal
          isOpen={showAppointmentModal}
          onClose={() => setShowAppointmentModal(false)}
          therapist={therapist}
        />
      </CardContent>
    </Card>
  );
};


const TherapistList = () => {
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [displayedTherapists, setDisplayedTherapists] = useState<Therapist[]>([]);
  const [allTherapists, setAllTherapists] = useState<Therapist[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [primaryTherapist, setPrimaryTherapist] = useState<{
    therapist: { id: string; name: string; imageUrl?: string; specialties: Array<{ id: string; name: string }> };
    hasUpcomingSession: boolean;
    nudge?: string;
  } | null>(null);
  const [showQuickBookModal, setShowQuickBookModal] = useState(false);

  useEffect(() => {
    const loadTherapists = async () => {
      try {
        setIsLoading(true);
        const therapists = await fetchTherapists();
        setAllTherapists(therapists);
        setDisplayedTherapists(therapists);
      } catch (err) {
        setError('Une erreur est survenue lors du chargement des professionnels.');
        console.error('Error fetching therapists:', err);
      } finally {
        setIsLoading(false);
      }
    };

    const loadPrimaryTherapist = async () => {
      try {
        const data = await fetchPrimaryTherapist();
        setPrimaryTherapist(data);
      } catch (err) {
        console.error('Error fetching primary therapist:', err);
      }
    };

    loadTherapists();
    loadPrimaryTherapist();
  }, []);

  useEffect(() => {
    if (selectedSpecialties.length === 0) {
      setDisplayedTherapists(allTherapists);
      return;
    }

    const filtered = allTherapists.filter(therapist =>
      therapist.specialties.some(specialty =>
        selectedSpecialties.includes(specialty.id)
      )
    );
    setDisplayedTherapists(filtered);
  }, [selectedSpecialties, allTherapists]);

  const toggleSpecialty = (specialtyId: string) => {
    setSelectedSpecialties(prev =>
      prev.includes(specialtyId)
        ? prev.filter(id => id !== specialtyId)
        : [...prev, specialtyId]
    );
  };

  const clearFilters = () => {
    setSelectedSpecialties([]);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00c4cc]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 pb-12">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#00c4cc] to-[#008ba3] text-white pt-12 pb-24 px-4 md:px-8">
        <div className="max-w-5xl mx-auto text-center md:text-left">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Trouvez votre spécialiste</h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Des professionnels qualifiés pour vous accompagner vers votre mieux-être.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 -mt-12">
        {error && (
          <Alert variant="destructive" className="mb-6 shadow-md bg-white">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Quick Book Primary Therapist CTA */}
        {primaryTherapist && !primaryTherapist.hasUpcomingSession && (
          <Card data-testid="quick-book-cta" className="mb-8 shadow-xl border border-gray-100 overflow-hidden bg-white">
            <CardContent className="p-5 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
                  <TherapistAvatar
                    imageUrl={primaryTherapist.therapist.imageUrl}
                    name={primaryTherapist.therapist.name}
                    size="sm"
                    className="border-2 border-[#00c4cc]/20"
                  />
                  <div className="min-w-0">
                    <p className="text-gray-500 text-xs md:text-sm font-medium">
                      Votre thérapeute{primaryTherapist.nudge ? ` \u00b7 ${primaryTherapist.therapist.name}` : ''}
                    </p>
                    {primaryTherapist.nudge ? (
                      <p className="text-gray-700 text-sm md:text-base italic leading-snug mt-0.5">
                        &laquo;&nbsp;{primaryTherapist.nudge}&nbsp;&raquo;
                      </p>
                    ) : (
                      <h3 className="text-gray-900 text-base md:text-lg font-bold leading-snug">
                        {primaryTherapist.therapist.name}
                      </h3>
                    )}
                  </div>
                </div>
                <Button
                  className="w-full md:w-auto bg-[#00c4cc] hover:bg-[#00adc0] text-white font-semibold shadow-md flex-shrink-0"
                  onClick={() => setShowQuickBookModal(true)}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Réserver une séance
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {showQuickBookModal && primaryTherapist && (
          <AppointmentModal
            isOpen={showQuickBookModal}
            onClose={() => setShowQuickBookModal(false)}
            therapist={{
              id: primaryTherapist.therapist.id,
              userId: primaryTherapist.therapist.id,
              name: primaryTherapist.therapist.name,
              imageUrl: primaryTherapist.therapist.imageUrl || '',
              specialties: primaryTherapist.therapist.specialties.map(s => ({ id: s.id, name: s.name })),
              email: '',
              firstName: primaryTherapist.therapist.name,
              type: 'therapist' as const,
              bio: '',
              rating: 0,
              yearsExperience: 0,
              nextAvailable: '',
              languages: [],
            }}
          />
        )}

        {/* Filters Bar */}
        <Card className="mb-8 shadow-lg border-none">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto pb-2 md:pb-0 no-scrollbar">
                <Button
                  variant={showFilters ? "secondary" : "ghost"}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filtres {selectedSpecialties.length > 0 && `(${selectedSpecialties.length})`}
                </Button>
                {selectedSpecialties.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-gray-500 hover:text-red-500"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Effacer
                  </Button>
                )}
              </div>
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Rechercher par nom..."
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00c4cc]/20 focus:border-[#00c4cc] transition-all text-sm"
                />
              </div>
            </div>

            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-100 animate-fadeIn">
                <p className="text-sm font-medium text-gray-700 mb-3">Filtrer par spécialité :</p>
                <div className="flex flex-wrap gap-2">
                  {specialties.map((specialty) => (
                    <Badge
                      key={specialty.id}
                      className={`cursor-pointer px-3 py-1.5 transition-all duration-200 ${selectedSpecialties.includes(specialty.id)
                        ? 'bg-[#00c4cc] text-white hover:bg-[#00adc0]'
                        : 'bg-white border border-gray-200 text-gray-600 hover:border-[#00c4cc] hover:text-[#00c4cc]'
                        } font-normal`}
                      onClick={() => toggleSpecialty(specialty.id)}
                    >
                      {specialty.name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {displayedTherapists.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center text-gray-500">
              <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">Aucun résultat trouvé</h3>
              <p className="mb-4">Essayez de modifier vos filtres ou effectuez une nouvelle recherche.</p>
              <Button
                variant="link"
                onClick={clearFilters}
                className="text-[#00c4cc]"
              >
                Tout effacer
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {displayedTherapists.map((therapist) => (
              <TherapistCard key={therapist.id} therapist={therapist} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TherapistList;